import Payment from "@/components/payment";
import InitPayment from "@/components/InitPayment/InitPayment";
import poweredByDark from "@/assets/powered-by-dark.png";
import poweredByLight from "@/assets/powered-by-light.png";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Spinner } from "@/components/ui/spinner";
import { useQuery } from "@tanstack/react-query";
import { getPayment, getPosPayment } from "@/api/payment";
import { useEffect, useState } from "react";

function App() {
  // Get the paymentId from the url
  const urlParams = new URLSearchParams(window.location.search);
  const paymentId =
    urlParams.get("id") || urlParams.get("bpId") || urlParams.get("state");
  const posId = urlParams.get("posId");
  const [refetchInterval, setRefetchInterval] = useState(750);
  const [showErrorAlert, setShowErrorAlert] = useState<boolean>(false);

  const { data: posPayment } = useQuery({
    queryKey: ["posPayment", { posId }],
    queryFn: () => (posId !== null ? getPosPayment(posId) : null),
    enabled: !!posId,
    refetchInterval: (query) => (query.state.data?.id ? false : 2000),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const isWaitingForPosPaymentId =
    !!posId && !paymentId && !posPayment?.id && !showErrorAlert;

  // Polling payment
  const { data: pollPaymentData, error: pollPaymentError, isPending: isPollingPaymentPending } = useQuery({
    queryKey: ["pollPayment", { id: paymentId }, posPayment?.id],
    queryFn: () => {
      // If this is a normal payment
      if (paymentId) {
        return getPayment(paymentId);
      }
      // Pos payment
      if (posPayment?.id) {
        // If we have a posId in the url we now change it now to the paymentId, this triggers a re-fetch of the payment query via poll payment queryKey
        changeUrlPaymentId(String(posPayment?.id));
      }
      return null;
    },
    refetchInterval,
    enabled: !!paymentId || !!posPayment?.id,
    retry: (_, error) => {
      // We want the network request to stop if the response is an error. We don't need the counter for now.
      if (error) {
        return false;
      }
      return true;
    },
  });

  const changeUrlPaymentId = (newPaymentId: string) => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("id", newPaymentId);
    newUrl.searchParams.delete("posId");
    window.history.replaceState({}, "", newUrl.toString());
  };

  useEffect(() => {
    if (pollPaymentData) {
      // If the payment is in DRAFT or SCHEDULED mode we stop the polling.
      if (["DRAFT"].includes(pollPaymentData.status)) {
        setRefetchInterval(0);
        setShowErrorAlert(false);
      }

      if (
        ["ERROR", "REJECTED", "SUCCESS", "CANCELLED", "SCHEDULED"].includes(
          pollPaymentData.status,
        )
      ) {
        // Reset error alert for successful payments
        if (pollPaymentData.status === "SUCCESS") {
          setShowErrorAlert(false);
        }
        // Partner redirect url can be missing. If it's missing we show a fallback text.
        if (!pollPaymentData.partnerRedirectUrl || pollPaymentData.partnerRedirectUrl === "") {
          // Stop the polling
          setRefetchInterval(0);
        }
        const redirectUrl = pollPaymentData.partnerRedirectUrl;
        if (redirectUrl && redirectUrl !== "") {
          // Stop the polling
          setRefetchInterval(0);
          setShowErrorAlert(false);
          const urlToRedirect = redirectUrl; // Capture for closure
          setTimeout(() => {
            window.location.replace(urlToRedirect);
          }, 1500);
        }
      }
    }
  }, [pollPaymentData]);

  useEffect(() => {
    if (pollPaymentError) {
      setRefetchInterval(0);
      setShowErrorAlert(true);
    }
  }, [pollPaymentError]);

  // Auto-redirect DRAFT payments that require onboarding via scaRedirectUrl.
  // Guard against self-redirect when ecom recomputes the URL to point back here.
  useEffect(() => {
    if (
      pollPaymentData?.status === "DRAFT" &&
      pollPaymentData?.scaRedirectUrl &&
      !pollPaymentData.scaRedirectUrl.includes(window.location.origin)
    ) {
       if (window.location.href.includes("localhost")){
        console.warn("localhost URL detected in scaRedirectUrl, skipping auto-redirect to avoid potential redirect loop in development environment.");
        return;
      }
      window.location.href = pollPaymentData.scaRedirectUrl;
    }
  }, [pollPaymentData?.status, pollPaymentData?.scaRedirectUrl]);

  // If we enter the page with no query parameters
  if (urlParams.size === 0 && paymentId === null) {
    return (
      <div className="flex flex-col items-center p-8 h-full">
        <div className="flex flex-col gap-4 w-full items-center max-w-md h-full">
          <Alert variant={"default"} className="border-none shadow-none">
            <AlertTitle>
              <div className="flex justify-center gap-2 text-center">
                Takk fyrir að nota tafarlausa millifærslu
              </div>
            </AlertTitle>
            <AlertDescription className="text-md text-center justify-items-center">
              {"Þú mátt loka síðunni"}
            </AlertDescription>
          </Alert>
        </div>
        <div className="mt-auto flex justify-center pt-4">
          <img
            src={poweredByLight}
            alt="Powered by Blikk"
            className="block dark:hidden h-12 w-auto"
          />
          <img
            src={poweredByDark}
            alt="Powered by Blikk"
            className="hidden dark:block h-12 w-auto"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center pt-8 px-8 pb-0 h-full bg-background text-foreground overflow-y-auto">
      <div className="flex flex-col gap-4 w-full items-center max-w-md h-full min-h-0">
        {showErrorAlert && !posId && !isPollingPaymentPending && (
          <Alert variant={"destructive"} className="border-none shadow-none">
            <AlertTitle>
              <div className="flex justify-center gap-2 text-center">Athugið</div>
            </AlertTitle>
            <AlertDescription className="text-md text-center justify-items-center">
              {"Villa kom upp við að framkvæma greiðslu"}
            </AlertDescription>
          </Alert>
        )}
        {isWaitingForPosPaymentId && (
          <div className="flex flex-col items-center justify-center w-full gap-4 py-8">
            <Alert variant={"default"} className="border-none shadow-none ">
              <AlertTitle>
                <div className="flex justify-center gap-2 text-center">
                  Áttu eftir að velja að borga með Blikk?
                </div>
              </AlertTitle>
              <AlertDescription className="text-md text-center justify-items-center">
                Verið er að sækja greiðslu...
              </AlertDescription>
            </Alert>
            <Spinner className="size-6"/>
          </div>
        )}
        {pollPaymentData?.status === "SCHEDULED" && (
          <div className="flex flex-col items-center justify-center w-full gap-4 ">
            <div className="flex flex-col gap-4 w-full items-center max-w-md h-full">
              <Alert variant={"default"} className="border-none shadow-none">
                <AlertTitle>
                  <div className="flex justify-center gap-2 text-center">Athugið</div>
                </AlertTitle>
                <AlertDescription className="text-md text-center justify-items-center">
                  {"Framvirk greiðsla hefur verið stofnuð inn í heimabankanum"}
                </AlertDescription>
              </Alert>
            </div>
          </div>
        )}
        {!isWaitingForPosPaymentId && pollPaymentData?.status === "DRAFT" && (
          <InitPayment
            payment={pollPaymentData}
            setRefetchInterval={(value) => setRefetchInterval(value)}
          />
        )}
        {!isWaitingForPosPaymentId &&
          pollPaymentData?.status !== "DRAFT" &&
          pollPaymentData?.status !== "SCHEDULED" && (
            <Payment data={pollPaymentData} error={showErrorAlert} isPending={isPollingPaymentPending} />
          )}
        {/* Logo at bottom: pb-8 is the screen's bottom padding, inside scroll so it's always visible */}
        <div className="mt-auto flex justify-center pt-4 pb-8 shrink-0">
          <img
            src={poweredByLight}
            alt="Powered by Blikk"
            className="block dark:hidden h-12 w-auto"
          />
          <img
            src={poweredByDark}
            alt="Powered by Blikk"
            className="hidden dark:block h-12 w-auto"
          />
        </div>
      </div>
    </div>
  );
}

export default App;
